import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { getAccessLogs, isPrimaryOccupant } from "./TenantAuth";
import { useTenantHeader } from "./TenantLayout";
import { normalizeAccessLog, officerLabel } from "../../utils/accessLogs";

// Visit history for the signed-in tenant or primary occupant: everyone who came through the
// gate on their code, who is still inside, and who has overstayed a temporary pass.
const PAGE_SIZE = 15;

const STATUS_FILTERS = {
  all: {},
  inside: { isActiveVisit: true },
  overstay: { isOverstay: true },
  completed: { isActiveVisit: false },
};

function fmtStamp(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit", hour12: true });
}

function fmtDuration(fromIso, toIso) {
  if (!fromIso) return "";
  const mins = Math.max(0, Math.round((new Date(toIso || Date.now()) - new Date(fromIso)) / 60000));
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ${mins % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

function splitWords(s) {
  return String(s || "").replace(/([a-z])([A-Z])/g, "$1 $2");
}

function visitStatus(log) {
  if (log.checkedOutAt) return { label: "Checked out", color: "#718096" };
  if (log.isOverstay) return { label: "Overstayed", color: "#DC2626" };
  return { label: "Inside", color: "#16A34A" };
}

function StatusChip({ color, children }) {
  return (
    <span className="badge rounded-pill fw-semibold fs-9 px-3 py-2" style={{ background: `${color}14`, color }}>
      <span className="d-inline-block rounded-circle me-2" style={{ width: 6, height: 6, background: color }} />
      {children}
    </span>
  );
}

const KPIS = [
  { key: "total", icon: "ki-entrance-left", label: "Total Visits", sub: "All gate check-ins" },
  { key: "inside", icon: "ki-home-2", label: "Inside Now", sub: "Not checked out yet" },
  { key: "overstay", icon: "ki-time", label: "Overstayed", sub: "Temporary pass expired" },
];

export default function TenantVisits() {
  const { setHeaderConfig } = useTenantHeader();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ total: null, inside: null, overstay: null });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setHeaderConfig({ title: "Visit History", breadcrumb: "Visits", actions: null });
    return () => setHeaderConfig({ title: "", breadcrumb: "", actions: null });
  }, [setHeaderConfig]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Headline counts: three one-row queries read only their totalCount.
  const loadCounts = useCallback(async () => {
    try {
      const [all, inside, overstay] = await Promise.all([
        getAccessLogs({ pageSize: 1 }),
        getAccessLogs({ pageSize: 1, isActiveVisit: true }),
        getAccessLogs({ pageSize: 1, isOverstay: true }),
      ]);
      setCounts({ total: all.totalCount, inside: inside.totalCount, overstay: overstay.totalCount });
    } catch {
      // The list below reports the error.
    }
  }, []);

  const loadPage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAccessLogs({
        pageIndex: page,
        pageSize: PAGE_SIZE,
        search: debounced,
        ...STATUS_FILTERS[status],
      });
      setRows(res.data.map(normalizeAccessLog));
      setTotal(res.totalCount);
    } catch (err) {
      toast.error(err.message || "Failed to load visit history.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, debounced, status]);

  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => { loadPage(); }, [loadPage]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hostNoun = isPrimaryOccupant() ? "your household" : "your home";

  return (
    <div className="app-container container-fluid py-6 px-6 px-lg-10">
      <div className="d6-kpi-brand p-6 mb-5">
        <div className="row g-4 g-xl-0 align-items-center">
          {KPIS.map((k, i) => (
            <div key={k.key} className="col-12 col-sm-4">
              <div className={`px-4 py-2 h-100 ${i < KPIS.length - 1 ? "border-end-sm border-white border-opacity-20" : ""}`}>
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className={`ki-outline ${k.icon} fs-2 text-white`} />
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">{k.label}</div>
                    <div className="d6-num text-white my-0.5">{counts[k.key] ?? "—"}</div>
                    <div className="text-white text-opacity-70 fs-9">{k.sub}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {counts.overstay > 0 && (
        <div className="d6-card p-4 mb-5 d-flex gap-3 align-items-center" style={{ border: "1px solid #fecaca", background: "#fef2f2" }}>
          <i className="ki-outline ki-information-5 fs-2" style={{ color: "#dc2626" }} />
          <div className="fs-8" style={{ color: "#b91c1c" }}>
            <strong>{counts.overstay} {counts.overstay === 1 ? "guest is" : "guests are"} still inside after their pass expired.</strong>{" "}
            Estate security can check them out at the gate.
          </div>
          <button type="button" className="btn btn-sm btn-light-danger ms-auto flex-shrink-0" onClick={() => { setStatus("overstay"); setPage(1); }}>
            Show them
          </button>
        </div>
      )}

      <div className="d6-card p-6 mb-5">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-5">
          <div>
            <h3 className="fs-5 fw-bold text-gray-900 mb-1">Gate Activity</h3>
            <div className="fs-8 text-gray-500">Everyone who entered the estate on an access code from {hostNoun}.</div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="position-relative" style={{ minWidth: 220 }}>
              <i className="ki-outline ki-magnifier fs-4 text-gray-400 position-absolute top-50 start-0 translate-middle-y ms-3" />
              <input
                type="text"
                className="d6m-input ps-10"
                style={{ minHeight: 40, height: 40, borderRadius: 999 }}
                placeholder="Search name or code…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <select
              className="d6m-input"
              style={{ width: 150, minHeight: 40, height: 40, borderRadius: 999 }}
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            >
              <option value="all">All visits</option>
              <option value="inside">Inside now</option>
              <option value="overstay">Overstayed</option>
              <option value="completed">Checked out</option>
            </select>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table align-middle table-row-dashed fs-7 gy-4 mb-0">
            <thead>
              <tr className="text-start text-gray-500 fw-bold fs-8 text-uppercase">
                <th className="min-w-175px">Guest</th>
                <th className="min-w-100px">Code</th>
                <th className="min-w-150px">Checked in</th>
                <th className="min-w-150px">Checked out</th>
                <th className="min-w-110px text-end">Status</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}><td colSpan={5}><div className="placeholder-glow"><span className="placeholder col-12 rounded" style={{ height: 22 }} /></div></td></tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12">
                    <i className="ki-outline ki-shield-tick fs-3x text-gray-300 d-block mb-3" />
                    <div className="fw-bold text-gray-700">No visits found</div>
                    <div className="fs-8 text-gray-500">Gate check-ins on your access codes will appear here.</div>
                  </td>
                </tr>
              ) : (
                rows.map((l) => {
                  const st = visitStatus(l);
                  const name = l.subjectName || l.dependentName || l.visitorName || "Guest";
                  return (
                    <tr key={l.id}>
                      <td>
                        <div className="fw-bold text-gray-900">{name}</div>
                        <div className="fs-8 text-gray-500">
                          {[splitWords(l.dependentType || l.subjectType), l.accessPoint].filter(Boolean).join(" · ")}
                        </div>
                      </td>
                      <td className="fw-semibold">{l.code || "—"}</td>
                      <td>
                        <div>{fmtStamp(l.checkedInAt)}</div>
                        {l.checkedInAt && <div className="fs-8 text-gray-500">{officerLabel(l, "in")}</div>}
                      </td>
                      <td>
                        <div>{l.checkedOutAt ? fmtStamp(l.checkedOutAt) : "Still inside"}</div>
                        <div className="fs-8 text-gray-500">
                          {l.checkedOutAt ? officerLabel(l, "out") : l.checkedInAt ? `${fmtDuration(l.checkedInAt)} so far` : ""}
                        </div>
                      </td>
                      <td className="text-end"><StatusChip color={st.color}>{st.label}</StatusChip></td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center pt-5">
            <div className="fs-8 text-gray-500">Page {page} of {totalPages} · {total} visits</div>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-sm btn-light" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Previous</button>
              <button type="button" className="btn btn-sm btn-light" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
